import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, AlertTitle, Button, CircularProgress, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Radio, RadioGroup, Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import AppDialog from '../../../shared/components/AppDialog';
import { applyApiFieldErrors } from '../../../shared/api';
import { describeOverviewAnomaly } from '../../emissions/model/emissionScreens';
import { REPORT_EXPORT_MODES } from '../model/reportModel';

export default function ReportExportDialog({
  open,
  exporting,
  isAdmin,
  hasData,
  anomalies = [],
  onExport,
  onClose,
}) {
  const { t } = useTranslation();
  const [exportMode, setExportMode] = useState(REPORT_EXPORT_MODES.STANDARD);
  const [fieldError, setFieldError] = useState('');
  const [rootError, setRootError] = useState('');
  const anomalyDetails = anomalies
    .map(describeOverviewAnomaly)
    .filter(detail => detail.userVisible);

  useEffect(() => {
    if (!open || !isAdmin) setExportMode(REPORT_EXPORT_MODES.STANDARD);
    setFieldError('');
    setRootError('');
  }, [isAdmin, open]);

  const handleExport = async () => {
    try {
      await onExport(exportMode);
    } catch (error) {
      let nextFieldError = '';
      let nextRootError = '';
      applyApiFieldErrors(error, (field, detail) => {
        if (['mode', 'export_mode', 'exportMode'].includes(field)) {
          if (!nextFieldError) nextFieldError = detail.message;
        } else {
          if (!nextRootError) nextRootError = detail.message;
        }
      }, t);
      setFieldError(nextFieldError);
      setRootError(nextRootError);
    }
  };

  return (
    <AppDialog open={open} onClose={exporting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t('reports.exportDialog.title')}  
        <IconButton
          onClick={onClose}
          disabled={exporting}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {rootError && <Alert severity="error">{rootError}</Alert>}
          <RadioGroup
            value={exportMode}
            onChange={(event) => {
              setExportMode(event.target.value);
              setFieldError('');
            }}
            aria-invalid={Boolean(fieldError)}
            aria-describedby={fieldError ? 'report-export-mode-error' : undefined}
          >
            <FormControlLabel
              value={REPORT_EXPORT_MODES.STANDARD}
              disabled={exporting}
              control={<Radio />}
              label={(
                <Stack spacing={0.25}>
                  <Typography variant="body2">{t('reports.exportDialog.standard')}</Typography>
                </Stack>
              )}
            />
            {isAdmin && (
              <FormControlLabel
                value={REPORT_EXPORT_MODES.FULL}
                disabled={exporting}
                control={<Radio />}
                label={(
                  <Stack spacing={0.25}>
                    <Typography variant="body2">{t('reports.exportDialog.full')}</Typography>
                  </Stack>
                )}
              />
            )}
          </RadioGroup>
          {fieldError && (
            <Typography id="report-export-mode-error" color="error" variant="caption" role="alert">
              {fieldError}
            </Typography>
          )}
          {!hasData && (
            <Alert severity="info">{t('reports.exportDialog.emptyPolicy')}</Alert>
          )}
          {anomalyDetails.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>{t('reports.exportDialog.dataNoticeTitle')}</AlertTitle>
              <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2 }}>
                {anomalyDetails.map((detail) => (
                  <Typography component="li" variant="body2" key={detail.raw}>
                    {t(detail.labelKey, { target: detail.target, code: detail.raw })}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
          </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={exporting}>
          {t(exportMode === REPORT_EXPORT_MODES.FULL ? 'common.cancel' : 'common.close')}
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={exporting
            ? <CircularProgress color="inherit" size={16} />
            : <DownloadRoundedIcon />}
          onClick={handleExport}
          disabled={exporting || !exportMode}
        >
          {exporting ? t('reports.exporting') : t('reports.exportDialog.export')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}
